<script lang="ts">
  import { CheckCircle, XCircle } from 'lucide-svelte';
  import { fade, scale } from 'svelte/transition';
  import type { ExerciseItem, EvaluationResult, UserAnswer, MultipleChoiceAnswer } from '../../types/exercise';
  import { _ } from 'svelte-i18n';

  interface Props {
    exercise: ExerciseItem;
    showFeedback: boolean;
    evaluationResult: EvaluationResult | null;
    onAnswerChange: (answer: UserAnswer) => void;
  }

  let { exercise, showFeedback, evaluationResult, onAnswerChange }: Props = $props();

  // Local state
  let selectedIndex = $state<number | null>(null);

  // Get mode-specific data
  let modeData = $derived(
    exercise.modeData.type === 'MULTIPLE_CHOICE' ? exercise.modeData : null
  );

  // Reset state when exercise changes
  $effect(() => {
    if (exercise.id) {
      selectedIndex = null;
    }
  });

  // Handle option selection
  function selectOption(index: number) {
    if (showFeedback) return;

    selectedIndex = index;
    const answer: MultipleChoiceAnswer = {
      type: 'MULTIPLE_CHOICE',
      selectedIndex: index,
    };
    onAnswerChange(answer);
  }

  // Get option styles
  function getOptionClass(index: number): string {
    const base = 'w-full p-4 rounded-xl border-2 text-left transition-all duration-200 font-medium relative';

    // Enhanced background for better visibility in both modes
    // Using bg-secondary/10 provides a solid but subtle background that works on both dark and light
    let backgroundClass = 'bg-secondary/10 hover:bg-secondary/20 border-border/50';

    if (!showFeedback) {
      if (selectedIndex === index) {
        return `${base} border-primary shadow-lg shadow-primary/30`;
      }
      return `${base} ${backgroundClass} hover:border-primary/50`;
    }

    // Feedback mode
    if (!modeData) return base;

    const isCorrectOption = index === modeData.correctIndex;
    const isSelected = selectedIndex === index;

    if (isCorrectOption) {
      return `${base} border-green-500 bg-green-100 dark:bg-green-500/40 text-green-900 dark:text-green-50 shadow-[0_0_10px_rgba(34,197,94,0.3)]`;
    }

    if (isSelected && !isCorrectOption) {
      return `${base} border-red-500 bg-red-100 dark:bg-red-500/40 text-red-900 dark:text-red-50`;
    }

    return `${base} border-border/30 opacity-50 bg-background`;
  }
</script>

<div class="space-y-5">
  <!-- Prompt -->
  <div class="text-center" in:fade={{ duration: 300 }}>
    <h2 class="text-2xl font-bold tracking-tight mb-2 text-foreground">{exercise.prompt}</h2>
  </div>

  <!-- Options -->
  {#if modeData}
    <div class="space-y-3">
      {#each modeData.options as option, index}
        <button
          type="button"
          onclick={() => selectOption(index)}
          disabled={showFeedback}
          class={getOptionClass(index)}
          style={selectedIndex === index && !showFeedback ? 'background-color: hsl(var(--primary) / 0.35);' : ''}
        >
          <div class="flex items-center gap-4 relative z-10">
            <span
              class="flex items-center justify-center w-10 h-10 rounded-full text-base font-bold shadow-sm transition-transform duration-200 group-hover:scale-110
                     {selectedIndex === index
                       ? 'bg-primary text-primary-foreground shadow-md'
                       : 'bg-secondary text-secondary-foreground ring-1 ring-border'}"
            >
              {String.fromCharCode(65 + index)}
            </span>
            <span class="flex-1 text-lg text-foreground">{option}</span>

            {#if showFeedback}
              <div in:scale={{ duration: 300, start: 0.5 }}>
                {#if index === modeData.correctIndex}
                  <CheckCircle class="w-6 h-6 text-green-600 dark:text-green-400" />
                {:else if selectedIndex === index}
                  <XCircle class="w-6 h-6 text-red-600 dark:text-red-400" />
                {/if}
              </div>
            {/if}
          </div>
        </button>
      {/each}
    </div>
  {/if}

  <!-- Feedback -->
  {#if showFeedback && evaluationResult}
    <div
      in:scale={{ duration: 300, start: 0.95 }}
      class="p-5 rounded-xl shadow-lg border-2 backdrop-blur-sm
        {evaluationResult.isCorrect
        ? 'bg-green-100/80 dark:bg-green-500/30 border-green-500 text-green-900 dark:text-green-50'
        : 'bg-red-100/80 dark:bg-red-500/30 border-red-500 text-red-900 dark:text-red-50'}"
    >
      <div class="flex items-start gap-3">
        {#if evaluationResult.isCorrect}
          <div class="p-1 bg-green-200 dark:bg-green-800 rounded-full">
            <CheckCircle class="w-6 h-6 text-green-700 dark:text-green-300" />
          </div>
          <div>
            <h4 class="font-bold text-lg">{$_('exercise.correct')}</h4>
            <p class="opacity-90">{$_('exercise.great_job')}</p>
          </div>
        {:else}
          <div class="p-1 bg-red-200 dark:bg-red-800 rounded-full">
            <XCircle class="w-6 h-6 text-red-700 dark:text-red-300" />
          </div>
          <div>
            <h4 class="font-bold text-lg">{$_('exercise.incorrect')}</h4>
            <span class="font-semibold text-lg block mt-1">
              {$_('exercise.correct_answer_label')} {evaluationResult.correctAnswer}
            </span>
          </div>
        {/if}
      </div>
    </div>
  {/if}
</div>

